import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/publications', label: 'Publications' },
  { path: '/articles', label: 'Articles' },
  { path: '/certifications', label: 'Certifications' }
];

export const NavbarDrawer: React.FC<NavbarDrawerProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 998,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: isMobileView ? '100%' : '450px',
          height: '100vh',
          background: 'rgba(10, 10, 10, 0.85)',
          borderLeft: isMobileView ? 'none' : '1px solid rgba(245, 245, 245, 0.04)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          boxShadow: '-20px 0 60px rgba(0, 0, 0, 0.5)',
          zIndex: 999,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Navigation Items */}
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '36px',
          alignItems: 'flex-start',
          width: '100%',
          paddingLeft: isMobileView ? '40px' : '80px'
        }}>
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  textDecoration: 'none',
                  color: isActive ? '#F5F5F5' : '#737373',
                  fontSize: isMobileView ? '36px' : '48px',
                  fontWeight: 400,
                  fontFamily: "'Space Grotesk', sans-serif",
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: isOpen ? `${0.1 + index * 0.06}s` : '0s',
                  letterSpacing: '-0.02em',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = '#F5F5F5';
                    e.currentTarget.style.transform = 'translateX(8px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = '#737373';
                  }
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                {/* Active indicator — copper bar */}
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    left: '-24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '50%',
                    background: '#C4956A',
                    borderRadius: '3px'
                  }} />
                )}

                <span>{item.label}</span>

                <svg
                  width={isMobileView ? "20" : "24"}
                  height={isMobileView ? "20" : "24"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transition: 'all 0.3s ease',
                    opacity: isActive ? 0.8 : 0.3
                  }}
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            );
          })}
        </nav>

        {/* Bottom section — social & contact */}
        <div style={{
          position: 'absolute',
          bottom: '48px',
          left: isMobileView ? '40px' : '80px',
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.5s ease 0.4s',
        }}>
          <a
            href="mailto:asimhafeezz@icloud.com"
            style={{
              color: '#737373',
              fontSize: '14px',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              textDecoration: 'none',
              letterSpacing: '0.02em',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#C4956A'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#737373'; }}
          >
            asimhafeezz@icloud.com
          </a>
        </div>
      </div>
    </>
  );
};
